import type {
  Coupon,
  CouponRegistration,
  PreOrderProductDiscountEvent,
  ProductDiscountEvent,
} from '@mathflat/domain/PodOrder'
import { makeAutoObservable } from 'mobx'

import { podMallController } from '@/controller/podMall.controller'
import { commonErrorMessage, toastSuccess } from '@/utils/toast'

export default class PODMallCouponService {
  couponList: Coupon[] = []
  couponRandomCode: string | null = null
  constructor() {
    makeAutoObservable(this)
  }

  get couponCountStr() {
    const couponCount = this.couponList.length
    if (couponCount > 99) return '99+'
    if (couponCount === 0) return ''
    return `${couponCount}`
  }

  updateRandomCouponCode = (code: string) => {
    this.couponRandomCode = code
  }

  private async _fetchCouponList() {
    return await podMallController.getCoupon()
  }

  private async _fetchProductDiscountEvent() {
    const event = await podMallController.getProductDiscountEvent()
    return event ? toProductDiscountCoupon(event) : null
  }

  async fetchSetCouponList() {
    try {
      const [productDiscountEvent, couponList] = await Promise.all([
        this._fetchProductDiscountEvent(),
        this._fetchCouponList(),
      ])

      if (productDiscountEvent) {
        this.couponList = [productDiscountEvent, ...couponList]
      } else {
        this.couponList = couponList
      }
    } catch (error) {
      console.error(error)
    }
  }

  async fetchCouponListByCode({ code }: { code: string }) {
    return await podMallController.getCoupon({ code })
  }

  async registerCoupon(userCouponId: Coupon['id'], payload: CouponRegistration) {
    try {
      const data = await podMallController.postUserCouponByCode(userCouponId, payload)
      await this.fetchSetCouponList()
      toastSuccess('쿠폰이 추가되었습니다.')
      return data
    } catch (e) {
      commonErrorMessage(e)
    }
  }
}

export const PRODUCT_DISCOUNT_COUPON_ID = -1

// 요금제 상시 할인을 쿠폰 목록에 함께 보이려고 쿠폰 모양으로 만든다. 할인액은 주문서에 적용해야 정해지므로
// 주문서 단위 이벤트만 싣는다. 쿠폰함(사용자 단위)은 할인액을 그리지 않아 0 으로 둔다
export const toProductDiscountCoupon = (
  event: ProductDiscountEvent | PreOrderProductDiscountEvent,
): Coupon => {
  const discountAmount = 'discountAmount' in event ? event.discountAmount : undefined
  return {
    id: PRODUCT_DISCOUNT_COUPON_ID,
    name: event.name,
    benefitType: 'PRODUCT_DISCOUNT',
    benefitName: event.benefitName,
    benefitValue: discountAmount ?? 0,
    conditionType: null,
    conditionValue: null,
    conditionName: '',
    endDate: event.endDate,
    usageStatus: 'UNUSED',
    usable: true,
    discountAmount,
  }
}
